import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearTempPasscodeResult, issueTempPasscode, selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { ResetPasscodeModalStates, ResetPasswordModalProps } from '../ResetPassword';
import { TempPasscodeRequestDto } from '@inosio/payments-sdk';

export interface SubjectDetailsPassReset {
  ProfileUuid: string;
  UserUuid: string;
  Email: string;
  CreatedAt: string;
}

/**
 * This custom hook serves as an object factory (sort of) that packages the dependencies of the passwordResetModal
 * into one function so that the modal can easily be reused accross the application without duplicating code.
 * @param props all self explanatory but particular attention should be paid to handleCloseModal,
 * this is used to clean up the states and may mean different things depending on the context... for e.g in the use case for the resident, we carry out 2 steps:
 * 1. reset the currentSelection i.e setSelectedResident({})
 * 2. turn off the control flags for the modal, e.g setOpenInvitationModal(false);
 * @returns all the parameters needed to use the passwordResetModal.
 */
export function usePasswordResetConfigs(props: { handleCloseModal: () => void; openModal: boolean; subjectDetails: SubjectDetailsPassReset }): ResetPasswordModalProps {
  const dispatch = useDispatch();
  const { tempPasscodeDetails, isIssuingTempPasscodeSuccess, isIssuingTempPasscodeFailed, issuingTempPasscodeError } = useSelector(selectAuthState);
  const [tempPasscode, setTempPasscode] = React.useState('');
  const [tempPasscodeError, setTempPasscodeError] = React.useState('');
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [modalState, setModalState] = React.useState(ResetPasscodeModalStates.UserRegistration);

  useEffect(() => {
    if (isIssuingTempPasscodeFailed) {
      setIsSpinnerModalOpen(false);
      setTempPasscodeError(issuingTempPasscodeError);
    }
  }, [isIssuingTempPasscodeFailed]);

  useEffect(() => {
    if (isIssuingTempPasscodeSuccess) {
      setSpinnerStatus('stop');
      setTempPasscode(tempPasscodeDetails?.temp_passcode);
      setModalState(ResetPasscodeModalStates.ResetPassword);
    }
  }, [isIssuingTempPasscodeSuccess]);

  const handleTempPasscode = (profileUuid: string, userUuid: string) => {
    dispatch({ type: issueTempPasscode.type });
    setTempPasscodeError('');
    setIsSpinnerModalOpen(true);

    dispatch({
      type: issueTempPasscode.type,
      payload: { UserUuid: userUuid, ProfileUuid: profileUuid } as TempPasscodeRequestDto,
    });
  };

  const handleCloseResetPasscodeModal = () => {
    props.handleCloseModal();
    setTempPasscode('');
    setTempPasscodeError('');
    setModalState(ResetPasscodeModalStates.UserRegistration);
    dispatch({ type: clearTempPasscodeResult.type });
  };

  return {
    modalState,
    setModalState,
    handleTempPasscode,
    tempPasscode,
    tempPasscodeError,
    isSpinnerModalOpen,
    setIsSpinnerModalOpen,
    spinnerStatus,
    subjectDetails: props.subjectDetails,
    open: props.openModal,
    handleClose: handleCloseResetPasscodeModal,
  };
}
